/**
 * LiveKit Stream Publisher Component
 * Used for solo streaming - publishes video/audio to LiveKit
 * Supports screenshare toggle while live
 */

import React, { useCallback, useEffect, useState, useRef, useImperativeHandle, forwardRef } from 'react';
import {
  LiveKitRoom,
  VideoTrack,
  useLocalParticipant,
  useTracks,
  RoomAudioRenderer,
  useRoomContext,
} from '@livekit/components-react';
import { Track, RoomEvent, createLocalScreenTracks, createLocalVideoTrack } from 'livekit-client';
import '@livekit/components-styles';

// Stable references — a new object per render makes LiveKitRoom re-apply
// camera/mic every render (mic flapping, video flicker, PC churn).
const LK_VIDEO_OPTS = { resolution: { width: 1280, height: 720, frameRate: 30 }, facingMode: 'user' };
const LK_AUDIO_OPTS = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
const LK_ROOM_OPTS = {
  adaptiveStream: true, dynacast: true,
  publishDefaults: { videoEncoding: { maxBitrate: 2_500_000, maxFramerate: 30 }, simulcast: true },
};

// Inner component that uses LiveKit hooks (must be inside LiveKitRoom)
const PublisherInner = forwardRef(({ onPublishSuccess, onPublishError, onScreenShareChange }, ref) => {
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled } = useLocalParticipant();
  const room = useRoomContext();
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenTrack, setScreenTrack] = useState(null);
  const [originalCameraTrack, setOriginalCameraTrack] = useState(null);
  const originalCameraTrackRef = useRef(null);
  
  const tracks = useTracks([
    { source: Track.Source.Camera, withPlaceholder: true },
    { source: Track.Source.ScreenShare, withPlaceholder: false },
    { source: Track.Source.Microphone, withPlaceholder: true },
  ]);
  
  const videoTrack = tracks.find(t => t.source === Track.Source.Camera)?.publication?.track;
  const screenShareTrack = tracks.find(t => t.source === Track.Source.ScreenShare)?.publication?.track;
  const activeVideoTrack = screenShareTrack || videoTrack;
  
  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    /**
     * Bug 6 (May 7 testing): parent calls publisherRef.current.setMicEnabled(bool)
     * to actually mute/unmute the LiveKit local participant. Pre-fix the
     * parent had no hook into LiveKit's mic state and the mute button was
     * a no-op for solo streams.
     */
    setMicEnabled: async (enabled) => {
      if (!localParticipant) {
        console.error('[Bug6] LiveKit setMicEnabled: no local participant');
        return null;
      }
      try {
        await localParticipant.setMicrophoneEnabled(!!enabled);
        console.log('[Bug6] LiveKit mic enabled =', !!enabled);
        return !!enabled;
      } catch (err) {
        console.error('[Bug6] LiveKit setMicrophoneEnabled failed:', err);
        return null;
      }
    },
    isMicEnabled: () => !!isMicrophoneEnabled,
    toggleScreenShare: async () => {
      if (!localParticipant || !room) {
        console.error('Cannot toggle screenshare: no local participant or room');
        return false;
      }

      try {
        if (isScreenSharing) {
          // Stop screenshare - unpublish screen track and republish camera
          console.log('🖥️ Stopping screenshare...');
          
          if (screenTrack) {
            await localParticipant.unpublishTrack(screenTrack);
            screenTrack.stop();
            setScreenTrack(null);
          }
          
          // Re-enable camera (use the ref — state can be stale within this call)
          const savedCam = originalCameraTrackRef.current;
          if (!localParticipant.getTrackPublication(Track.Source.Camera)) {
            if (savedCam) {
              await localParticipant.publishTrack(savedCam);
            } else {
              const newCameraTrack = await createLocalVideoTrack({ facingMode: 'user', resolution: { width: 1280, height: 720 } });
              await localParticipant.publishTrack(newCameraTrack);
            }
          }
          originalCameraTrackRef.current = null;
          setOriginalCameraTrack(null);
          
          setIsScreenSharing(false);
          if (onScreenShareChange) onScreenShareChange(false);
          console.log('✅ Screenshare stopped, camera restored');
          return false;
        } else {
          // Start screenshare
          console.log('🖥️ Starting screenshare...');
          
          // Save current camera track before unpublishing
          const currentCameraPublication = localParticipant.getTrackPublication(Track.Source.Camera);
          if (currentCameraPublication?.track) {
            originalCameraTrackRef.current = currentCameraPublication.track;
            setOriginalCameraTrack(currentCameraPublication.track);
            await localParticipant.unpublishTrack(currentCameraPublication.track);
          }
          
          // Create and publish screen track
          const screenTracks = await createLocalScreenTracks({
            audio: true, // Include system audio if possible
            video: {
              displaySurface: 'monitor', // or 'window', 'browser'
              logicalSurface: true,
              cursor: 'always',
              width: 1920,
              height: 1080,
              frameRate: 30,
            },
          });
          
          const videoScreenTrack = screenTracks.find(t => t.kind === 'video');
          if (videoScreenTrack) {
            await localParticipant.publishTrack(videoScreenTrack, {
              source: Track.Source.ScreenShare,
              name: 'screen',
            });
            setScreenTrack(videoScreenTrack);
            
            // Handle when user stops sharing via browser UI
            videoScreenTrack.mediaStreamTrack.onended = async () => {
              console.log('🖥️ User stopped screenshare via browser');
              await localParticipant.unpublishTrack(videoScreenTrack);
              videoScreenTrack.stop();
              setScreenTrack(null);
              
              // Restore camera (ref-based; state may be stale in this closure)
              const savedCamOnEnded = originalCameraTrackRef.current;
              if (!localParticipant.getTrackPublication(Track.Source.Camera)) {
                if (savedCamOnEnded) {
                  await localParticipant.publishTrack(savedCamOnEnded);
                } else {
                  const newCameraTrack = await createLocalVideoTrack({ facingMode: 'user', resolution: { width: 1280, height: 720 } });
                  await localParticipant.publishTrack(newCameraTrack);
                }
              }
              originalCameraTrackRef.current = null;
              setOriginalCameraTrack(null);
              
              setIsScreenSharing(false);
              if (onScreenShareChange) onScreenShareChange(false);
            };
          }
          
          // Publish audio track from screen if available
          const audioScreenTrack = screenTracks.find(t => t.kind === 'audio');
          if (audioScreenTrack) {
            await localParticipant.publishTrack(audioScreenTrack, {
              source: Track.Source.ScreenShareAudio,
              name: 'screen_audio',
            });
          }
          
          setIsScreenSharing(true);
          if (onScreenShareChange) onScreenShareChange(true);
          console.log('✅ Screenshare started');
          return true;
        }
      } catch (error) {
        console.error('❌ Screenshare toggle error:', error);
        // User cancelled the screen picker (or it failed). The camera was
        // already unpublished before the picker opened, so restore it now.
        // Read from the ref — state set earlier in THIS call is stale here.
        try {
          const savedCam = originalCameraTrackRef.current;
          const hasCam = !!localParticipant.getTrackPublication(Track.Source.Camera);
          if (!hasCam) {
            if (savedCam) {
              await localParticipant.publishTrack(savedCam);
            } else {
              const freshCam = await createLocalVideoTrack({ facingMode: 'user', resolution: { width: 1280, height: 720 } });
              await localParticipant.publishTrack(freshCam);
            }
          }
        } catch (e) {
          console.error('Failed to restore camera after cancel:', e);
        }
        originalCameraTrackRef.current = null;
        setOriginalCameraTrack(null);
        setIsScreenSharing(false);
        if (onScreenShareChange) onScreenShareChange(false);
        return false;
      }
    },
    isScreenSharing: () => isScreenSharing,
  }));
  
  useEffect(() => {
    if (localParticipant) {
      console.log('✅ LiveKit: Local participant connected', localParticipant.identity);
      if (onPublishSuccess) {
        onPublishSuccess({
          identity: localParticipant.identity,
          isCameraEnabled,
          isMicrophoneEnabled
        });
      }
    }
  }, [localParticipant, isCameraEnabled, isMicrophoneEnabled, onPublishSuccess]);

  // Determine which track to display
  const displayTrack = isScreenSharing && screenShareTrack 
    ? tracks.find(t => t.source === Track.Source.ScreenShare)
    : tracks.find(t => t.source === Track.Source.Camera);

  return (
    <div className="w-full h-full bg-black relative">
      {/* Screenshare indicator */}
      {isScreenSharing && (
        <div className="absolute top-3 left-3 z-20 flex items-center gap-2 px-3 py-1.5 bg-blue-600 rounded-full">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
          <span className="text-white text-sm font-medium">Screen Sharing</span>
        </div>
      )}
      
      {displayTrack?.publication?.track ? (
        <VideoTrack 
          trackRef={displayTrack}
          className="w-full h-full object-contain"
          style={{ transform: isScreenSharing ? 'none' : 'scaleX(-1)' }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center text-white">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-lg">{isScreenSharing ? 'Starting screenshare...' : 'Starting camera...'}</p>
          </div>
        </div>
      )}
    </div>
  );
});

PublisherInner.displayName = 'PublisherInner';

const LiveKitStreamPublisher = forwardRef(({ 
  token, 
  serverUrl, 
  roomName, 
  onPublishSuccess, 
  onPublishError,
  onDisconnected,
  onScreenShareChange,
}, ref) => {
  const handleError = useCallback((error) => {
    console.error('❌ LiveKit room error:', error);
    if (onPublishError) {
      onPublishError(error);
    }
  }, [onPublishError]);

  const handleDisconnected = useCallback(() => {
    console.log('👋 LiveKit: Disconnected from room');
    if (onDisconnected) {
      onDisconnected();
    }
  }, [onDisconnected]);

  if (!token || !serverUrl) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-900">
        <div className="text-center text-white">
          <p className="text-xl mb-2">⚠️ LiveKit not configured</p>
          <p className="text-sm text-gray-400">Missing token or server URL</p>
        </div>
      </div>
    );
  }

  return (
    <LiveKitRoom
      token={token}
      serverUrl={serverUrl}
      connect={true}
      video={LK_VIDEO_OPTS}
      audio={LK_AUDIO_OPTS}
      options={LK_ROOM_OPTS}
      onError={handleError}
      onDisconnected={handleDisconnected}
      style={{ height: '100%', width: '100%' }}
    >
      <PublisherInner 
        ref={ref}
        onPublishSuccess={onPublishSuccess} 
        onPublishError={onPublishError}
        onScreenShareChange={onScreenShareChange}
      />
      <RoomAudioRenderer />
    </LiveKitRoom>
  );
});

LiveKitStreamPublisher.displayName = 'LiveKitStreamPublisher';

export default LiveKitStreamPublisher;
